import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';

import BeefreeSDK from '@beefree.io/sdk';
import { TokenStatus } from '@beefree.io/sdk/dist/types/bee';
import { useRecoilValue } from 'recoil';

import { useGetBeeFreeSettingsQuery } from 'src/app/queries/email-marketing/get-beefree-settings';
import { useGetBeeFreeTokenQuery } from 'src/app/queries/email-marketing/get-beefree-token';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';

type useBeeFreeSetupProps = {
  beeFreeContainerRef: MutableRefObject<HTMLDivElement | null>;
  containerId: string;
  enableAbandonedCartAddOn: boolean;
  isEnabled: boolean;
  onChange: () => void;
  onReady?: () => void;
  onSave: (json: string, html: string) => Promise<void>;
  templateJSON: Record<string, unknown>;
  waitForTemplateLoad: boolean;
};

type useBeeFreeSetupReturn = {
  beeFreeInstance: BeefreeSDK | null;
};

export function useBeeFreeSetup({
  beeFreeContainerRef,
  containerId,
  enableAbandonedCartAddOn,
  isEnabled,
  onChange,
  onReady,
  onSave,
  templateJSON,
  waitForTemplateLoad,
}: useBeeFreeSetupProps): useBeeFreeSetupReturn {
  const userDispensaries = useRecoilValue(userDispensariesAtom);

  const { data: beeFreeLspSettingsV2 } = useGetBeeFreeSettingsQuery({ enabled: true });
  const { data: beeFreeToken } = useGetBeeFreeTokenQuery({ enabled: true });
  const [isInitialized, setIsInitialized] = useState(false);

  const [beePluginInstance, setBeePluginInstance] = useState<BeefreeSDK | null>(null);

  useEffect(() => {
    if (beeFreeToken) {
      setBeePluginInstance(
        new BeefreeSDK({
          access_token: beeFreeToken ?? '',
          v2: true,
          status: TokenStatus.OK,
          shared: false,
          coediting_session_id: null,
        })
      );
    }
  }, [beeFreeToken]);

  useEffect(() => {
    // Forgive me for using let & any - this will be deleted post-rollout POS-6176
    const clientUid = beeFreeLspSettingsV2?.ClientUid;
    const aiIntegrationEnabled = beeFreeLspSettingsV2?.AiIntegrationEnabled;
    const editorFonts = {
      showDefaultFonts: true,
      customFonts: beeFreeLspSettingsV2?.CustomFonts || [],
    };

    if (
      !beeFreeContainerRef?.current ||
      waitForTemplateLoad ||
      !beePluginInstance ||
      !beeFreeLspSettingsV2 ||
      !isEnabled
    ) {
      return;
    }

    const MERGE_TAGS = [
      { name: 'First name', value: '{{FirstName}}', previewValue: 'Kim' },
      { name: 'Last name', value: '{{LastName}}', previewValue: 'Kitsuragi' },
      { name: 'Loyalty points', value: '{{LoyaltyPoints}}', previewValue: '100' },
      { name: 'Birthday', value: '{{Birthday}}', previewValue: 'July 15' },
      { name: 'State', value: '{{State}}', previewValue: 'CO' },
      { name: 'First purchase date', value: '{{FirstTransactionDate}}', previewValue: 'January 1, 2024' },
      { name: 'Last purchase date', value: '{{LastTransactionDate}}', previewValue: 'March 3, 2025' },
      { name: 'Med card expiration date', value: '{{MJIdExpirationDate}}', previewValue: 'November 10, 2025' },
      { name: 'Loyalty tier name', value: '{{LoyaltyTierName}}', previewValue: 'Gold' },
      { name: 'Loyalty enrollment date', value: '{{LoyaltyRegistrationDate}}', previewValue: 'November 10, 2025' },
      {
        name: 'Latest loyalty redemption date',
        value: '{{LatestRedemptionDate}}',
        previewValue: 'November 10, 2025',
      },
      { name: 'Loyalty tier reached year', value: '{{EstablishedTierBaseYear}}', previewValue: '2024' },
      { name: 'Loyalty tier accrual rate', value: '{{LoyaltyTierAccrualRate}}', previewValue: '1.5' },
      ...(enableAbandonedCartAddOn
        ? [
            {
              name: 'Abandoned cart URL',
              value: '{{AbandonedCartUrl}}',
              previewValue: 'https://example.com/cart/complete',
            },
          ]
        : []),
    ];

    // can't use relative path because call comes from BF iframe
    const cssUrl = `${window.location.protocol}//${window.location.host}/assets/custom-beefree-styles.css`;

    const beeConfig = {
      container: containerId, // identifies the id of the div element that contains BEE Plugin.
      uid: clientUid,
      customCss: cssUrl,
      language: 'en-US',
      autosave: 1,
      mergeTags: MERGE_TAGS,
      trackChanges: true,
      loadingSpinnerDisableOnSave: true,
      editorFonts,
      onChange,
      onSave: (jsonFile, htmlFile) => {
        void onSave(jsonFile, htmlFile);
      },
      onStart: onReady,
      addOns: [
        {
          id: 'ai-integration',
          enabled: aiIntegrationEnabled ?? false,
          settings: {
            isPromptDisabled: false,
            isSuggestionsDisabled: true,
            submitOnEnterKey: true,
          },
        },
        {
          id: 'abandoned-cart',
          enabled: enableAbandonedCartAddOn ?? false,
          openOnDrop: true,
          editable: false,
        },
      ],
      contentDialog: {
        addOn: {
          label: 'Insert Content',
          handler: async (resolve, reject, args) => {
            if (args.contentDialogId === 'abandoned-cart') {
              resolve({
                type: 'mixed',
                value: [
                  {
                    type: 'paragraph',
                    value: {
                      html: `
                          <div id='isDynamicCampaignAddOn' data-bee-non-editable contenteditable="false" style="padding: 20px; margin: 20px 0; background-color: #ffffff; color: #031f38;">
                            <p style="margin: 0 0 15px 0; text-align: left; font-weight: bold; font-size: 19px;">Here's the items you left behind.</p>
                            
                            <!-- Header Row -->
                            <div style="padding: 0; color: #9da3a8; font-size: 0;">
                              <div style="display: inline-block; width: 60%; font-weight: bold; padding-right: 10px; vertical-align: top; font-size: 14px;">&nbsp;</div>
                              <div style="display: inline-block; width: 10%; font-weight: bold; text-align: center; padding: 0 10px; vertical-align: top; font-size: 12px;">QTY</div>
                              <div style="display: inline-block; width: 20%; font-weight: bold; text-align: right; padding-left: 10px; vertical-align: top; font-size: 12px;">PRICE</div>
                            </div>
                            
                            <!-- Cart Items -->
                            {{#each cart.items}}
                            <div style="padding: 20px 0; border-bottom: 1px solid #ddd; font-size: 0;">
                              <!-- Product Info Section -->
                              <div style="display: inline-block; width: 60%; padding-right: 10px; vertical-align: top;">
                                <div style="display: inline-block; width: 80px; height: 80px; background-color: #E0E0E0; background-image: url('{{this.image}}'); background-size: cover; background-position: center; margin-right: 15px; vertical-align: top;"></div>
                                <div style="display: inline-block; width: calc(100% - 95px); vertical-align: top; margin: 10px 0 0 0;">
                                  <p style="margin: 0 0 8px 0; text-align: left; font-weight: bold; color: #031f38; font-size: 16px; word-wrap: break-word; overflow-wrap: break-word;">{{this.name}}</p>
                                  <p style="margin: 0 0 8px 0; text-align: left; color: #768897; font-size: 13px;">{{this.brand}}</p>
                                  <p style="margin: 0; text-align: left; color: #768897; font-size: 13px;">{{this.weight}}</p>
                                  </div>
                              </div>
                              
                              <!-- Quantity -->
                              <div style="display: inline-block; width: 10%; text-align: center; padding: 0 10px; margin: 10px 0 0 0; color: #768897; font-size: 13px; vertical-align: top;">{{this.quantity}}</div>
                              <!-- Price -->
                              <div style="display: inline-block; width: 20%; text-align: right; padding-left: 10px; margin: 10px 0 0 0; color: #768897; font-size: 13px; vertical-align: top;">\${{this.price}}</div>
                            </div>
                            {{/each}}
                            <p style="color: #646D72; font-size: 10px; margin-top: 15px;">
                              Prices and availability are subject to change
                            </p>
                          </div>
                        `,
                    },
                  },
                  {
                    type: 'button',
                    value: {
                      label: 'Complete Order',
                      href: '{{AbandonedCartUrl}}',
                      'background-color': '#0066CC',
                      color: '#FFFFFF',
                      'border-radius': 4,
                      'padding-top': 15,
                      'padding-right': 40,
                      'padding-bottom': 15,
                      'padding-left': 40,
                      'font-size': 16,
                    },
                  },
                ],
              });
            } else {
              reject('editing not handled yet');
            }
          },
        },
      },
    };

    const initializeBeeEditor = async () => {
      try {
        if (!isInitialized) {
          await beePluginInstance.start(beeConfig, templateJSON);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error(error);
      }
    };

    void initializeBeeEditor();
  }, [
    containerId,
    beeFreeContainerRef,
    waitForTemplateLoad,
    enableAbandonedCartAddOn,
    beeFreeLspSettingsV2,
    userDispensaries.selectedCompany,
    onChange,
    onReady,
    onSave,
    templateJSON,
    isEnabled,
    beeFreeToken,
    beePluginInstance,
    isInitialized,
  ]);

  return {
    beeFreeInstance: beePluginInstance,
  };
}
